import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SiteSetting {
  id: string;
  setting_key: string;
  setting_value: string;
  description: string | null;
  category: string;
  created_at: string;
  updated_at: string;
}

export function useSiteSettings() {
  const queryClient = useQueryClient();

  const { data: settings = [], isLoading, error } = useQuery({
    queryKey: ["site-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("site_settings")
        .select("*")
        .order("category", { ascending: true });

      if (error) throw error;
      return data as SiteSetting[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutos de cache
  });

  const getSetting = (key: string, fallback: string = ""): string => {
    const setting = settings.find((s) => s.setting_key === key);
    return setting?.setting_value ?? fallback;
  };

  const getSettingsByCategory = (category: string): SiteSetting[] => {
    return settings.filter((s) => s.category === category);
  };

  const updateSettingMutation = useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }) => {
      const { error } = await supabase
        .from("site_settings")
        .update({ setting_value: value })
        .eq("setting_key", key);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-settings"] });
    },
  });

  const createSettingMutation = useMutation({
    mutationFn: async (newSetting: Omit<SiteSetting, "id" | "created_at" | "updated_at">) => {
      const { error } = await supabase
        .from("site_settings")
        .insert(newSetting);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-settings"] });
    },
  });

  const deleteSettingMutation = useMutation({
    mutationFn: async (key: string) => {
      const { error } = await supabase
        .from("site_settings")
        .delete()
        .eq("setting_key", key);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["site-settings"] });
    },
  });

  return {
    settings,
    isLoading,
    error,
    getSetting,
    getSettingsByCategory,
    updateSetting: updateSettingMutation.mutate,
    createSetting: createSettingMutation.mutate,
    deleteSetting: deleteSettingMutation.mutate,
    isUpdating: updateSettingMutation.isPending,
    isCreating: createSettingMutation.isPending,
    isDeleting: deleteSettingMutation.isPending,
  };
}
